import { useState } from "react"
import { Button } from "@aix/ui"
import toast from "react-hot-toast"
import { trpc } from "~/lib/trpc"

interface EndpointConfig {
  name: string
  description: string
  exampleInput: string
  procedure: string
}

const ENDPOINTS: EndpointConfig[] = [
  {
    name: "listTrackables",
    description: "Get trackables for specific window",
    exampleInput: JSON.stringify({ checkInWindow: "morning" }, null, 2),
    procedure: "trackables.listTrackables",
  },
  {
    name: "getCheckInData",
    description: "Get trackables + entries for a date",
    exampleInput: JSON.stringify({ date: new Date().toISOString().split("T")[0] }, null, 2),
    procedure: "trackables.getCheckInData",
  },
  {
    name: "getEntriesByDate",
    description: "Get all entries for a date",
    exampleInput: JSON.stringify({ date: new Date().toISOString().split("T")[0] }, null, 2),
    procedure: "trackables.getEntriesByDate",
  },
  {
    name: "getAllTrackables",
    description: "Get all trackables (no window filter)",
    exampleInput: JSON.stringify({}, null, 2),
    procedure: "trackables.getAllTrackables",
  },
  {
    name: "getTrackablesSchema",
    description: "Get DB schema information",
    exampleInput: JSON.stringify({}, null, 2),
    procedure: "trackables.getTrackablesSchema",
  },
  {
    name: "getEntriesStats",
    description: "Get aggregated entry statistics",
    exampleInput: JSON.stringify(
      {
        dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        dateTo: new Date().toISOString().split("T")[0],
      },
      null,
      2,
    ),
    procedure: "trackables.getEntriesStats",
  },
  // Note: Mutations (createTrackable, archiveTrackable, etc.) cannot be tested via utils.fetch()
  // Use the UI components to test mutations instead
]

export default function EndpointTester() {
  const [selectedEndpoint, setSelectedEndpoint] = useState<EndpointConfig>(ENDPOINTS[0])
  const [inputParams, setInputParams] = useState<string>(ENDPOINTS[0].exampleInput)
  const [response, setResponse] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [executionTime, setExecutionTime] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Move useUtils to top level (Rules of Hooks)
  const utils = trpc.useUtils()

  const handleEndpointChange = (endpoint: EndpointConfig) => {
    setSelectedEndpoint(endpoint)
    setInputParams(endpoint.exampleInput)
    setResponse(null)
    setError(null)
    setExecutionTime(null)
  }

  const handleExecute = async () => {
    setIsLoading(true)
    setError(null)
    setResponse(null)
    const startTime = performance.now()

    try {
      // Parse input params
      const params = JSON.parse(inputParams)

      // Call the appropriate tRPC procedure
      let result

      switch (selectedEndpoint.name) {
        case "listTrackables":
          result = await utils.trackables.listTrackables.fetch(params)
          break
        case "getCheckInData":
          result = await utils.trackables.getCheckInData.fetch(params)
          break
        case "getEntriesByDate":
          result = await utils.trackables.getEntriesByDate.fetch(params)
          break
        case "getAllTrackables":
          result = await utils.trackables.getAllTrackables.fetch()
          break
        case "getTrackablesSchema":
          result = await utils.trackables.getTrackablesSchema.fetch()
          break
        case "getEntriesStats":
          result = await utils.trackables.getEntriesStats.fetch(params)
          break
        default:
          throw new Error("Unknown endpoint")
      }

      const endTime = performance.now()
      setExecutionTime(endTime - startTime)
      setResponse(result)
      toast.success("Request successful")
    } catch (err: any) {
      setError(err.message || "Request failed")
      toast.error("Request failed")
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyResponse = () => {
    if (response) {
      void navigator.clipboard.writeText(JSON.stringify(response, null, 2))
      toast.success("Response copied to clipboard")
    }
  }

  // Validate JSON
  let isValidJson = true
  try {
    JSON.parse(inputParams)
  } catch {
    isValidJson = false
  }

  return (
    <div className="space-y-4">
      {/* Endpoint Selector */}
      <div>
        <label className="block text-sm font-semibold mb-2">Select Endpoint:</label>
        <select
          value={selectedEndpoint.name}
          onChange={(e) => {
            const endpoint = ENDPOINTS.find((ep) => ep.name === e.target.value)
            if (endpoint) handleEndpointChange(endpoint)
          }}
          className="w-full border-2 border-border p-2 bg-background"
        >
          {ENDPOINTS.map((endpoint) => (
            <option key={endpoint.name} value={endpoint.name}>
              {endpoint.name} - {endpoint.description}
            </option>
          ))}
        </select>
      </div>

      {/* Input Parameters */}
      <div>
        <label className="block text-sm font-semibold mb-2">
          Input Parameters (JSON):
          {!isValidJson && <span className="text-destructive ml-2">Invalid JSON</span>}
        </label>
        <textarea
          value={inputParams}
          onChange={(e) => setInputParams(e.target.value)}
          className="w-full h-32 border-2 border-border p-2 font-mono text-sm bg-background"
          placeholder="Enter JSON parameters..."
        />
      </div>

      {/* Execute Button */}
      <div>
        <Button onClick={handleExecute} loading={isLoading} disabled={!isValidJson} className="border-2 border-border">
          ▶️ Execute Request
        </Button>
      </div>

      {/* Response Display */}
      {(response || error) && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-4">
              <span className="text-sm font-semibold">Response:</span>
              {executionTime && <span className="text-xs text-muted-foreground">{executionTime.toFixed(0)}ms</span>}
              {error ? (
                <span className="text-xs px-2 py-1 bg-destructive text-destructive-foreground border-2 border-border">
                  ERROR
                </span>
              ) : (
                <span className="text-xs px-2 py-1 bg-primary text-primary-foreground border-2 border-border">
                  200 OK
                </span>
              )}
            </div>
            {response && (
              <Button onClick={handleCopyResponse} variant="ghost" size="sm">
                📋 Copy
              </Button>
            )}
          </div>
          <div className="bg-background border-2 border-border p-4 max-h-96 overflow-auto">
            {error ? (
              <div className="text-destructive text-sm">{error}</div>
            ) : (
              <pre className="text-xs">{JSON.stringify(response, null, 2)}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
